import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthProvider";
import { useLanguage } from "../../LanguageProvider";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../services/supabase";
import toast from "react-hot-toast";
import { ConfirmModal } from "../common/ConfirmModal";
import { SelectionModal } from "../common/SelectionModal";
import { SuccessModal } from "../common/SuccessModal";
import { ProfileModal } from "../common/ProfileModal";


import {
  Users,
  Calendar,
  LogOut,
  AlertCircle,
  Globe,
  Plus,
  Trash2,
  Sun,
  Moon,
  Check,
  CheckCircle,
  Feather,
  X,
  Settings,
  Pencil,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";

/* Google "G" logo as inline SVG for the sign-in button */
const GoogleIcon = () => (
  <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export const Sidebar = ({ sidebarOpen, setSidebarOpen, sessions, allParticipants, allUnpaidUsers, overdueUnpaidUsers, onDataRefresh, isDark, toggleTheme }) => {
  const { user, isAuthenticated, signInWithGoogle, signOut, userProfile, swishNumber, isNewUser, setIsNewUser, linkedParticipantId } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const [newSession, setNewSession] = useState({ date: getTodayDate(), name: "", swish_number: "", adminFeeEnabled: false, adminFeePercent: "" });
  const [preloadDefaults, setPreloadDefaults] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessionToArchive, setSessionToArchive] = useState(null);
  const [archiveStatusFlag, setArchiveStatusFlag] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  // Close sidebar on mobile when outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close sidebar if clicking inside a modal or portal
      if (event.target.closest('.modal-overlay-wrapper') || 
          event.target.closest('.modal-container') ||
          event.target.closest('.confirm-modal') ||
          event.target.closest('.profile-modal-overlay')) {
        return;
      }

      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (sidebarOpen && window.innerWidth <= 768) {
          setSidebarOpen(false);
        }
      }
    };
    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [sidebarOpen, setSidebarOpen]);

  // Auto-open profile modal on first-time sign-in
  useEffect(() => {
    if (isNewUser && isAuthenticated) {
      setShowProfileModal(true);
      setIsNewUser(false); // Clear so it doesn't re-trigger
    }
  }, [isNewUser, isAuthenticated, setIsNewUser]);

  const handleGoogleSignIn = async () => {
    if (isSigningIn) return;
    setIsSigningIn(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error(t("auth_error"));
      setIsSigningIn(false);
    }
    // On success, the page will redirect to Google — no need to reset state
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      toast.success(t("logout_success"));
    } else {
      toast.error(t("auth_error"));
    }
  };

  // Check if the current user owns a specific session
  const isSessionOwner = (session) => {
    return isAuthenticated && user?.id === session?.created_by;
  };

  const createSession = async (e) => {
    e.preventDefault();
    if (!newSession.date || isCreating) return;
    setIsCreating(true);

    const adminFeePercent = newSession.adminFeeEnabled
      ? parseFloat(newSession.adminFeePercent) || 0
      : 0;

    const { data: sessionData, error: sessionError } = await supabase
      .from("sessions")
      .insert([
        {
          session_date: newSession.date,
          session_name: newSession.name || `${t("session_default_name")} ${newSession.date}`,
          swish_number: newSession.swish_number || null,
          created_by: user.id,
          admin_fee_percent: adminFeePercent,
        },
      ])
      .select()
      .single();

    if (!sessionError && sessionData) {
      if (preloadDefaults) {
        // Preload default expenses
        const defaultExpenses = [
          {
            session_id: sessionData.id,
            description: t("shuttlecock"),
            item_name: t("shuttlecock"),
            total_amount: 300,
            total_quantity: 12,
            used_quantity: 0,
            expense_type: "box"
          },
          {
            session_id: sessionData.id,
            description: `${t("field")} - 4 ${t("hour_short")}`,
            item_name: t("field"),
            total_amount: 940, // 4 hours * 235 kr
            total_quantity: 4,
            used_quantity: 0,
            expense_type: "field"
          }
        ];

        await supabase.from("expenses").insert(defaultExpenses);
      }

      setNewSession({ date: getTodayDate(), name: "", swish_number: "", adminFeeEnabled: false, adminFeePercent: "" });
      setShowAddSession(false);
      onDataRefresh();
      toast.success(t("session_created"));
      setIsCreating(false);

      // Close sidebar on mobile after creation and delay navigation slightly to prevent layout breaking
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
        setTimeout(() => {
          navigate(`/sessions/${sessionData.id}`);
          window.scrollTo(0, 0); // Reset scroll to top of new page
        }, 300);
      } else {
        navigate(`/sessions/${sessionData.id}`);
        window.scrollTo(0, 0);
      }
    } else {
      setIsCreating(false);
      toast.error(t("error_occurred"));
    }
  };

  const confirmArchiveSession = async (targetId = null, statusFlag = null) => {
    const finalId = targetId || sessionToArchive;
    const finalStatus = statusFlag || archiveStatusFlag;

    if (!finalId) return;

    setIsArchiving(true);
    // Always close confirmation modal immediately for better responsiveness
    setShowConfirm(false);

    try {
      if (finalStatus === 'completed') {
        const { error } = await supabase.rpc('archive_session', { target_session_id: finalId, status_flag: 'completed' });
        if (!error) {
          // Only navigate if we are currently looking at this specific session
          if (location.pathname === `/sessions/${finalId}`) {
            navigate("/");
          }
          setShowSuccessModal(true);
          onDataRefresh();
          setTimeout(() => setShowSuccessModal(false), 2000);
        } else {
          throw error;
        }
      } else {
        const targetSession = sessions?.find(s => s.id === finalId);
        const participantCount = targetSession?.session_participants?.length || 0;

        let error;
        if (participantCount === 0) {
          const res = await supabase.from("sessions").delete().eq("id", finalId);
          error = res.error;
        } else {
          const res = await supabase.rpc('archive_session', { target_session_id: finalId, status_flag: 'deleted' });
          error = res.error;
        }

        if (!error) {
          // Only navigate if we are currently looking at this specific session
          if (location.pathname === `/sessions/${finalId}`) {
            navigate("/");
          }
          toast.success(t("session_deleted"));
          onDataRefresh();
        } else {
          throw error;
        }
      }
    } catch (err) {
      console.error("Archive/Delete error:", err);
      toast.error(t("error_occurred"));
    } finally {
      setSessionToArchive(null);
      setArchiveStatusFlag(null);
      setIsArchiving(false);
    }
  };

  const handleArchiveClick = (id, statusFlag) => {
    if (statusFlag === 'completed') {
      confirmArchiveSession(id, 'completed');
    } else {
      setSessionToArchive(id);
      setArchiveStatusFlag(statusFlag);
      setShowConfirm(true);
    }
  };

  const handleEditStart = (session) => {
    setEditingSessionId(session.id);
    setEditName(session.session_name || "");
    setEditDate(session.session_date);
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    if (isUpdating) return;
    setIsUpdating(true);

    const { error } = await supabase
      .from("sessions")
      .update({
        session_name: editName,
        session_date: editDate
      })
      .eq("id", editingSessionId);

    if (!error) {
      toast.success(t("save_success"));
      setEditingSessionId(null);
      onDataRefresh();
    } else {
      toast.error(t("error_occurred"));
    }
    setIsUpdating(false);
  };



  return (
    <motion.aside
      ref={sidebarRef}
      className={`sidebar ${sidebarOpen ? "open" : "closed"}`}
      drag={window.innerWidth <= 768 ? "y" : false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.4}
      style={{ y: 0 }}
      onDragEnd={(e, { offset, velocity }) => {
        if (window.innerWidth <= 768) {
          // If dragged down enough, or fast enough
          if (offset.y > 150 || velocity.y > 600) {
            setSidebarOpen(false);
          }
        }
      }}
    >
      {/* Desktop Edge Toggle — vertically centered on the right edge */}
      {window.innerWidth > 768 && (
        <button
          className="sidebar-edge-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title={sidebarOpen ? t("close_menu") : t("open_menu")}
          aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
        >
          {sidebarOpen ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
        </button>
      )}
      {/* Mobile Sheet Handle */}
      {window.innerWidth <= 768 && sidebarOpen && (
        <div className="sheet-handle-wrapper">
          <div className="sheet-handle" />
        </div>
      )}

      <div className="sidebar-header">
        {window.innerWidth > 768 && (
          <Link to="/" className="sidebar-logo">
            <div className="logo-icon-premium">
              <Feather size={22} />
            </div>
            <h2>{t("app_title")}</h2>
          </Link>
        )}
        <div className="sidebar-header-actions" style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginLeft: "auto" }}>
          {/* Theme Switch (Mobile only) */}
          {window.innerWidth <= 768 && (
            <div className="theme-switch-wrapper" style={{ width: 'auto', marginRight: '4px' }}>
              <div className="theme-switch" onClick={toggleTheme} style={{ width: '130px' }}>
                <div className={`theme-switch-indicator ${isDark ? 'dark' : 'light'}`} />
                <div className={`theme-switch-icon ${!isDark ? 'active' : ''}`}><Sun size={14} /><span style={{ marginLeft: '4px', fontSize: '10px', fontWeight: 600 }}>{t("light")}</span></div>
                <div className={`theme-switch-icon ${isDark ? 'active' : ''}`}><span style={{ marginRight: '4px', fontSize: '10px', fontWeight: 600 }}>{t("dark")}</span><Moon size={14} /></div>
              </div>
            </div>
          )}

          {/* Language Switcher */}
          <div style={{ position: "relative" }}>
            <button
              className="sidebar-toggle"
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setShowLanguageModal(true);
                } else {
                  setShowLanguageDropdown(!showLanguageDropdown);
                }
              }}
              title={t("change_language")}
            >
              <Globe size={20} />
            </button>
            <AnimatePresence>
              {showLanguageDropdown && window.innerWidth > 768 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="dropdown-menu-language"
                >
                  <button
                    onClick={() => { setLanguage("vi"); setShowLanguageDropdown(false); }}
                    className={language === "vi" ? "active" : ""}
                  >
                    <span>🇻🇳</span> Tiếng Việt
                  </button>
                  <button
                    onClick={() => { setLanguage("en"); setShowLanguageDropdown(false); }}
                    className={language === "en" ? "active" : ""}
                  >
                    <span>🇺🇸</span> English
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>

      {!sidebarOpen && window.innerWidth > 768 && (
        <div className="sidebar-minimized">
          <button
            className="sidebar-toggle-icon mt-auto"
            style={{ marginTop: 'auto' }}
            onClick={toggleTheme}
            title={isDark ? t("light") : t("dark")}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      )}

      {sidebarOpen && (
        <div
          className="sidebar-content"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isAuthenticated && (
            <button
              className="btn btn-primary btn-full"
              onClick={() => {
                if (!showAddSession && swishNumber) {
                  setNewSession(prev => ({ ...prev, swish_number: prev.swish_number || swishNumber }));
                }
                setShowAddSession(!showAddSession);
              }}
            >
              <Plus size={20} />
              {t("create_new_session")}
            </button>
          )}

          <AnimatePresence>
            {showAddSession && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="sidebar-form"
                onAnimationComplete={() => {
                  const el = document.querySelector('.sidebar-form');
                  if (el) el.style.overflow = 'visible';
                }}
              >
                <form onSubmit={createSession}>
                  <div className="form-section-header">
                    <label className="form-label-premium">{t("session_date")}</label>
                    <button
                      type="button"
                      className="btn-text-tiny"
                      onClick={() => setNewSession({ ...newSession, date: getTodayDate() })}
                    >
                      {t("today")}
                    </button>
                  </div>

                  <div className="input-wrapper-premium">
                    <input
                      type="date"
                      value={newSession.date}
                      onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                      className="input input-premium"
                      required
                    />
                    <div className="input-icon-overlay">
                      <Calendar size={16} />
                    </div>
                  </div>

                  <div className="input-group">
                    <label className="form-label-premium">{t("session_name_optional")}</label>
                    <input
                      type="text"
                      placeholder={t("session_name_optional")}
                      value={newSession.name}
                      onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                      className="input input-premium"
                    />
                  </div>

                  <div className="input-group">
                    <label className="form-label-premium">{t("swish_number")}</label>
                    <input
                      type="text"
                      placeholder="07x xxx xxxx"
                      value={newSession.swish_number}
                      onChange={(e) => setNewSession({ ...newSession, swish_number: e.target.value })}
                      className="input input-premium"
                    />
                  </div>

                  <div className="form-toggles-group">
                    <div className="toggle-group-premium">
                      <label className="toggle-switch-premium">
                        <input
                          type="checkbox"
                          checked={preloadDefaults}
                          onChange={(e) => setPreloadDefaults(e.target.checked)}
                        />
                        <span className="toggle-slider-premium"></span>
                      </label>
                      <span className="toggle-label-premium">{t("preload_defaults")}</span>
                    </div>

                    <div className="toggle-group-premium">
                      <label className="toggle-switch-premium">
                        <input
                          type="checkbox"
                          checked={newSession.adminFeeEnabled}
                          onChange={(e) => setNewSession({ ...newSession, adminFeeEnabled: e.target.checked, adminFeePercent: e.target.checked ? newSession.adminFeePercent : "" })}
                        />
                        <span className="toggle-slider-premium"></span>
                      </label>
                      <span className="toggle-label-premium">{t("toggle_admin_fee")}</span>
                    </div>
                  </div>

                  {newSession.adminFeeEnabled && (
                    <div className="admin-fee-config">
                      <div className="admin-fee-config-head">
                        <label className="form-label-premium">{t("admin_fee_label")}</label>
                      </div>
                      <div className="percent-field">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          inputMode="decimal"
                          placeholder="0"
                          value={newSession.adminFeePercent}
                          onChange={(e) => setNewSession({ ...newSession, adminFeePercent: e.target.value })}
                          className="input input-premium percent-input"
                        />
                        <span className="percent-suffix">%</span>
                      </div>
                      <p className="admin-fee-hint">{t("admin_fee_description")}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-full shadow-accent"
                    disabled={isCreating}
                  >
                    {isCreating ? t("loading") : t("create")}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          <Link
            to="/"
            className={`btn btn-secondary btn-full sidebar-btn ${location.pathname === '/' ? "active" : ""}`}
            style={{ textDecoration: 'none', marginBottom: 'var(--space-4)' }}
            onClick={() => {
              if (window.innerWidth <= 768) setSidebarOpen(false);
            }}
          >
            <Calendar size={20} />
            {t("sessions_list")}
          </Link>

          <div className="sidebar-section">
            {(() => {
              const userSessions = sessions?.filter(isSessionOwner) || [];
              return (
                <>
                  <h3 className="sidebar-section-title">
                    {t("your_sessions")} ({userSessions.length})
                  </h3>
                  <div className="sessions-sidebar-list">
                    <AnimatePresence initial={false}>
                      {userSessions.map((session) => (
                        <motion.div
                          layout
                          key={session.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95, x: -20, transition: { duration: 0.2 } }}
                          className={`session-sidebar-item ${location.pathname === `/sessions/${session.id}` ? "active" : ""} ${editingSessionId === session.id ? "editing" : ""}`}
                  transition={{
                    layout: { duration: 0.25, type: "spring", stiffness: 200, damping: 25 },
                    opacity: { duration: 0.2 }
                  }}
                >
                  <AnimatePresence mode="wait">
                    {editingSessionId === session.id ? (
                      <motion.form
                        key="edit"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        className="session-sidebar-edit-form"
                        onSubmit={handleUpdateSession}
                      >
                        <div className="edit-form-header">
                          <span className="edit-form-title">{t("edit")}</span>
                        </div>
                        <div className="edit-inputs-premium">
                          <div className="input-with-icon-tiny">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="input-premium-mini"
                              placeholder={t("session_name_optional")}
                              autoFocus
                            />
                            <Pencil size={12} className="input-icon-tiny" />
                          </div>
                          <div className="input-with-icon-tiny">
                            <input
                              type="date"
                              value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              className="input-premium-mini"
                            />
                            <Calendar size={12} className="input-icon-tiny" />
                          </div>
                        </div>
                        <div className="edit-actions-premium">
                          <button
                            type="button"
                            className="btn-edit-cancel"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setEditingSessionId(null);
                            }}
                          >
                            {t("cancel")}
                          </button>
                          <button
                            type="submit"
                            className="btn-edit-save"
                            disabled={isUpdating}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isUpdating ? <div className="spinner-tiny" /> : t("save")}
                          </button>
                        </div>
                      </motion.form>
                    ) : (
                      <motion.div
                        key="view"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="session-sidebar-view-content"
                      >
                        <Link
                          to={`/sessions/${session.id}`}
                          className="session-sidebar-info"
                          style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
                          onClick={(e) => {
                            if (window.innerWidth <= 768) setSidebarOpen(false);
                          }}
                        >
                          <span className="session-sidebar-name">
                            {session.session_name || new Date(session.session_date).toLocaleDateString(language === "vi" ? "vi-VN" : "en-US")}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="session-sidebar-date">
                              {new Date(session.session_date).toLocaleDateString(language === "vi" ? "vi-VN" : "en-US", { day: "numeric", month: "short" })}
                            </span>
                          </div>
                        </Link>
                        {isSessionOwner(session) && (
                          <div className="session-sidebar-actions-strip">
                            <button
                              type="button"
                              className="action-strip-btn edit"
                              title={t("edit")}
                              onClick={(e) => {
                                e.stopPropagation(); e.preventDefault();
                                handleEditStart(session);
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                            {(() => {
                              const participantCount = session.session_participants?.length || 0;
                              const hasUnpaid = allUnpaidUsers?.some(u => u.session_id === session.id);
                              const isFullyPaid = participantCount > 0 && !hasUnpaid;

                              if (isFullyPaid) {
                                return (
                                  <button
                                    type="button"
                                    className="action-strip-btn archive"
                                    title={t("archive_tooltip")}
                                    disabled={isArchiving}
                                    onClick={(e) => {
                                      e.stopPropagation(); e.preventDefault();
                                      handleArchiveClick(session.id, 'completed');
                                    }}
                                  >
                                    <CheckCircle size={14} />
                                  </button>
                                );
                              } else {
                                return (
                                  <button
                                    type="button"
                                    className="action-strip-btn delete"
                                    title={t("delete_tooltip")}
                                    disabled={isArchiving}
                                    onClick={(e) => {
                                      e.stopPropagation(); e.preventDefault();
                                      handleArchiveClick(session.id, 'deleted');
                                    }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                );
                              }
                            })()}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </>
              );
            })()}
          </div>

          <Link
            to="/users"
            className={`btn btn-secondary btn-full sidebar-btn ${location.pathname === '/users' ? "active" : ""}`}
            style={{ textDecoration: 'none' }}
            onClick={() => {
              if (window.innerWidth <= 768) setSidebarOpen(false);
            }}
          >
            <Users size={20} />
            {isAuthenticated ? t("add_player") : t("player_list")}
          </Link>

          <Link
            to="/debts"
            className={`btn btn-secondary btn-full sidebar-btn ${location.pathname === '/debts' ? "active" : ""} ${overdueUnpaidUsers?.length > 0 ? "has-debt" : ""}`}
            style={{ textDecoration: 'none' }}
            onClick={() => {
              if (window.innerWidth <= 768) setSidebarOpen(false);
            }}
          >
            <AlertCircle size={20} />
            {t("bad_debt")} ({overdueUnpaidUsers?.length || 0})
          </Link>

          <div className="sidebar-bottom-actions" style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "1rem" }}>
            {window.innerWidth > 768 && (
              <div className="theme-switch-wrapper">
                <div className="theme-switch" onClick={toggleTheme}>
                  <div className={`theme-switch-indicator ${isDark ? 'dark' : 'light'}`} />
                  <div className={`theme-switch-icon ${!isDark ? 'active' : ''}`}><Sun size={16} /><span style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 600 }}>{t("light")}</span></div>
                  <div className={`theme-switch-icon ${isDark ? 'active' : ''}`}><span style={{ marginRight: '6px', fontSize: '12px', fontWeight: 600 }}>{t("dark")}</span><Moon size={16} /></div>
                </div>
              </div>
            )}

            {!isAuthenticated ? (
              <button
                onClick={() => setShowAuthModal(true)}
                className="btn btn-secondary btn-full"
              >
                <LogOut size={20} style={{ transform: "scaleX(-1)" }} />
                {t("login")}
              </button>
            ) : (
              <>
                <div className="user-profile-pill user-profile-pill-clickable" onClick={() => setShowProfileModal(true)}>
                  <div className={`user-profile-avatar-wrapper ${linkedParticipantId ? 'avatar-ring-verified' : ''}`}>
                    {userProfile?.avatarUrl ? (
                      <img src={userProfile.avatarUrl} alt={userProfile.name} className="user-profile-avatar" />
                    ) : (
                      <div className="user-profile-avatar-fallback">
                        {userProfile?.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                    )}
                    {linkedParticipantId && <span className="verified-dot"><Check size={7} /></span>}
                  </div>
                  <div className="user-profile-info">
                    <span className="user-profile-name">{userProfile?.name}</span>
                    <span className="user-profile-email">{userProfile?.email}</span>
                  </div>
                  <button
                    className="user-profile-settings-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowProfileModal(true);
                    }}
                    title={t("profile_settings")}
                  >
                    <Settings size={16} />
                  </button>
                </div>
                <button
                  onClick={handleSignOut}
                  className="btn btn-danger btn-full"
                >
                  <LogOut size={20} />
                  {t("sign_out")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auth Modal (Google Sign-In) */}
      {createPortal(
        <AnimatePresence>
          {showAuthModal && (
            <div className="modal-overlay-wrapper centered">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="modal-backdrop"
                onClick={() => setShowAuthModal(false)}
              />
              <div className="modal-container">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  className="confirm-modal info"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="modal-close-btn"
                    onClick={() => setShowAuthModal(false)}
                  >
                    <X size={20} />
                  </button>

                  <div className="auth-modal-content">
                    <div className="auth-modal-icon">
                      <Feather size={28} />
                    </div>
                    <span className="auth-modal-brand">{t("app_title")}</span>
                    <p className="auth-modal-subtitle">{t("sign_in_subtitle")}</p>

                    <button
                      className="btn-google-signin"
                      onClick={handleGoogleSignIn}
                      disabled={isSigningIn}
                    >
                      <GoogleIcon />
                      {isSigningIn ? t("loading") : t("sign_in_with_google")}
                    </button>

                    <p className="auth-modal-note">{t("auth_public_note")}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <>
          {/* Action Confirmation Modal */}
          <ConfirmModal
            isOpen={showConfirm}
            onClose={() => setShowConfirm(false)}
            onConfirm={confirmArchiveSession}
            title={archiveStatusFlag === 'completed' ? t("archive_session_title") : t("delete_session_title")}
            message={archiveStatusFlag === 'completed' ? t("confirm_archive_session") : t("confirm_delete_session")}
            confirmText={archiveStatusFlag === 'completed' ? t("complete") : t("delete")}
            cancelText={t("cancel")}
            type={archiveStatusFlag === 'completed' ? "success" : "danger"}
          />

          {/* Language Selection Modal (Mobile) */}
          <SelectionModal
            isOpen={showLanguageModal}
            onClose={() => setShowLanguageModal(false)}
            title={t("change_language")}
            options={[
              { value: "vi", label: "Tiếng Việt", icon: "🇻🇳", active: language === "vi" },
              { value: "en", label: "English", icon: "🇺🇸", active: language === "en" }
            ]}
            onSelect={(val) => setLanguage(val)}
          />

          <SuccessModal
            isOpen={showSuccessModal}
            title={t("complete")}
            message={t("session_archived")}
          />

          {/* Profile Settings Modal */}
          <ProfileModal
            isOpen={showProfileModal}
            onClose={() => setShowProfileModal(false)}
            allParticipants={allParticipants || []}
          />
        </>,
        document.body
      )}
    </motion.aside>
  );
};
